import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axiosInstance from 'apis/axiosInstance';
import CommonButton from 'components/CommonButton/CommonButton';
import DefaultModal from 'components/Modals/DefaultModal';
import { renderToast } from 'components/Toast/Toast';
import useClinic from 'hooks/useClinic';
import useModal from 'hooks/useModal';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import PmsPractitionerScheduleToggleSection from './PmsPractitionerScheduleToggleSection/PmsPractitionerScheduleToggleSection';

interface GeneralPractitionerSettingModalProps {
  onClose: () => void;
}

interface FormData {
  isPmsPractitionerSchedulePrioritizedEnabled: boolean;
}

const GeneralPractitionerSettingModal: FC<
  GeneralPractitionerSettingModalProps
> = ({ onClose }) => {
  const { data: clinic, mutate } = useClinic();

  const { control, reset, formState, handleSubmit } = useForm<FormData>({
    defaultValues: { isPmsPractitionerSchedulePrioritizedEnabled: false },
  });

  useModal({ onClose, isLoading: formState.isSubmitting });

  useEffect(() => {
    if (!clinic) return;

    reset({
      isPmsPractitionerSchedulePrioritizedEnabled:
        clinic.isPmsPractitionerSchedulePrioritizedEnabled || false,
    });
  }, [clinic, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      await axiosInstance.patch(
        '/clinics/pms-practitioner-schedule-priority-setting',
        {
          isPmsPractitionerSchedulePrioritizedEnabled:
            data.isPmsPractitionerSchedulePrioritizedEnabled,
        }
      );
      await mutate();
      renderToast({
        type: 'success',
        message: 'Updated settings successfully',
      });
      onClose();
    } catch (error: any) {
      if (error.response?.status === BAD_REQUEST) {
        return renderToast({
          type: 'error',
          message: error.response?.data.message,
        });
      }

      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  return (
    <DefaultModal>
      <>
        <div className="w-[50rem] overflow-auto max-h-[80vh] pb-[10px]">
          <p className="text-20 leading-[3rem] font-bold pt-1.6 pb-1.2 pl-3 border-b border-light-grey">
            General Practitioner Settings
          </p>
          <div className="px-3 mt-2.4">
            <div className="flex flex-col gap-y-2.8 -mx-0.8">
              <PmsPractitionerScheduleToggleSection control={control} />
            </div>
          </div>
        </div>

        <div className="px-3 mt-2.4 pb-3.2 flex gap-x-1.6 justify-end">
          <CommonButton
            variant="secondary"
            onClick={onClose}
            disabled={formState.isSubmitting}
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isDirty}
            isLoading={formState.isSubmitting}
          >
            Save
          </CommonButton>
        </div>
      </>
    </DefaultModal>
  );
};

export default GeneralPractitionerSettingModal;
